import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import TicketDownloadModal from './TicketDownloadModal';

/**
 * Enhanced PhonePe Payment Modal with Webhook-based Flow
 * 
 * Modern payment flow:
 * 1. Opens PhonePe in popup window
 * 2. Polls backend for webhook updates
 * 3. Shows ticket immediately when payment succeeds
 * 4. 5-minute timeout with countdown
 */
const PhonePePaymentModalEnhanced = ({ 
  checkoutUrl, 
  bookingId,
  orderId, // Alternative prop name
  onSuccess, 
  onFailure, 
  onClose 
}) => {
  // Use bookingId or orderId (whichever is provided)
  const orderIdToUse = bookingId || orderId;
  const [status, setStatus] = useState('opening'); // opening, paying, checking, success, failed, timeout
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
  const [booking, setBooking] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [showTicket, setShowTicket] = useState(false);
  const pollIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const paymentWindowRef = useRef(null);

  useEffect(() => {
    openPaymentWindow();
    startCountdown();
    
    return () => {
      cleanup();
    };
  }, []);

  const openPaymentWindow = () => {
    console.log('[PhonePeModal] Opening payment window:', checkoutUrl);
    
    // Open PhonePe in a popup window
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    paymentWindowRef.current = window.open(
      checkoutUrl,
      'PhonePe Payment',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );
    
    if (!paymentWindowRef.current) {
      alert('Please allow popups for this site to complete payment');
      setStatus('failed');
      return;
    }
    
    setStatus('paying');
    
    // Start polling for payment status
    setTimeout(() => {
      startPolling();
    }, 3000); // Wait 3 seconds before starting to poll
  };

  const startCountdown = () => {
    countdownIntervalRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          handleTimeout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startPolling = () => {
    console.log('[PhonePeModal] Starting status polling for booking:', orderIdToUse);
    
    // Poll every 2 seconds
    pollIntervalRef.current = setInterval(async () => {
      await checkPaymentStatus();
    }, 2000);
    
    // Also check immediately
    checkPaymentStatus();
  };

  const checkPaymentStatus = async () => {
    try {
      setPollCount(prev => prev + 1);
      
      console.log(`[PhonePeModal] Checking payment status (attempt ${pollCount + 1})`);
      
      // Check PhonePe order status
      const response = await axios.get(
        `${API_BASE_URL}/api/phonepe-tsp/status/${orderIdToUse}`
      );

      const state = response.data.data.state;
      console.log('[PhonePeModal] Payment state:', state);

      if (state === 'SUCCESS') {
        // Payment successful!
        cleanup();
        setStatus('checking');
        
        // Close payment window
        if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
          paymentWindowRef.current.close();
        }
        
        // Fetch full booking details
        try {
          const bookingResponse = await axios.get(
            `${API_BASE_URL}/api/bookings/${orderIdToUse}`
          );
          setBooking(bookingResponse.data.booking);
          setStatus('success');
          setShowTicket(true); // Auto-show ticket
          
          if (onSuccess) {
            onSuccess(bookingResponse.data.booking);
          }
        } catch (bookingError) {
          console.error('[PhonePeModal] Error fetching booking:', bookingError);
          setStatus('success');
        }

      } else if (state === 'FAILED' || state === 'EXPIRED') {
        // Payment failed
        cleanup();
        setStatus('failed');
        
        if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
          paymentWindowRef.current.close();
        }
        
        if (onFailure) {
          onFailure({ state, bookingId: orderIdToUse });
        }
      }
      // If PENDING or INITIATED, continue polling

    } catch (error) {
      console.error('[PhonePeModal] Error checking status:', error);
      // Continue polling on error
    }
  };

  const handleTimeout = () => {
    console.log('[PhonePeModal] Payment timeout');
    cleanup();
    setStatus('timeout');
    
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      paymentWindowRef.current.close();
    }
  };

  const cleanup = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
  };

  const handleClose = () => {
    cleanup();
    if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
      paymentWindowRef.current.close();
    }
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '500px',
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          position: 'relative'
        }}>
          {/* Close button */}
          {status !== 'success' && (
            <button
              onClick={handleClose}
              style={{
                position: 'absolute',
                top: '15px',
                right: '15px',
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#64748b'
              }}
            >
              ×
            </button>
          )}

          {status === 'opening' && (
            <>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #06b6d4',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <h2 style={{ color: '#2d3748', marginBottom: '10px' }}>Opening Payment Window...</h2>
              <p style={{ color: '#64748b' }}>
                Please complete your payment in the popup window
              </p>
            </>
          )}

          {status === 'paying' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>📱</div>
              <h2 style={{ color: '#06b6d4', marginBottom: '10px' }}>Complete Your Payment</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                Please complete your payment in the PhonePe window
              </p>
              <div style={{
                background: '#f0f9ff',
                padding: '15px',
                borderRadius: '12px',
                marginBottom: '20px'
              }}>
                <p style={{ fontSize: '14px', color: '#0369a1', marginBottom: '8px' }}>
                  ⏱️ Time remaining: <strong>{formatTime(countdown)}</strong>
                </p>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0 }}>
                  Waiting for payment confirmation...
                </p>
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f4f6',
                borderTop: '3px solid #06b6d4',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }} />
            </>
          )}

          {status === 'checking' && (
            <>
              <div style={{
                width: '60px',
                height: '60px',
                border: '4px solid #f3f4f6',
                borderTop: '4px solid #10b981',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <h2 style={{ color: '#10b981', marginBottom: '10px' }}>Payment Received!</h2>
              <p style={{ color: '#64748b' }}>
                Loading your ticket...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>✅</div>
              <h2 style={{ color: '#10b981', marginBottom: '10px' }}>Payment Successful!</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                Your booking has been confirmed
              </p>
              {booking && (
                <div style={{
                  background: '#f0fdf4',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '20px',
                  textAlign: 'left'
                }}>
                  <p style={{ marginBottom: '8px' }}><strong>Booking ID:</strong> {booking.bookingId}</p>
                  <p style={{ marginBottom: '8px' }}><strong>Amount:</strong> ₹{booking.totalAmount}</p>
                  <p style={{ marginBottom: '0' }}><strong>Status:</strong> <span style={{ color: '#10b981' }}>Confirmed</span></p>
                </div>
              )}
              <button
                onClick={handleClose}
                style={{
                  background: 'linear-gradient(45deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Close
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>❌</div>
              <h2 style={{ color: '#ef4444', marginBottom: '10px' }}>Payment Failed</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                Your payment could not be processed
              </p>
              <button
                onClick={handleClose}
                style={{
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Try Again
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>⏱️</div>
              <h2 style={{ color: '#f59e0b', marginBottom: '10px' }}>Payment Timeout</h2>
              <p style={{ color: '#64748b', marginBottom: '20px' }}>
                Payment window timed out. Please check your booking status or try again.
              </p>
              <div style={{
                background: '#fffbeb',
                padding: '15px',
                borderRadius: '12px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <p style={{ marginBottom: '8px', fontSize: '14px' }}><strong>Booking ID:</strong> {bookingId}</p>
                <p style={{ marginBottom: '0', fontSize: '12px', color: '#64748b' }}>
                  Save this ID to check your booking status later
                </p>
              </div>
              <button
                onClick={handleClose}
                style={{
                  background: '#64748b',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Close
              </button>
            </>
          )}

          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>

      {/* Ticket Modal - Shows on top of payment modal */}
      {showTicket && booking && (
        <TicketDownloadModal
          isOpen={showTicket}
          onClose={() => {
            setShowTicket(false);
            handleClose();
          }}
          booking={booking}
          mode="display"
        />
      )}
    </>
  );
};

export default PhonePePaymentModalEnhanced;
